import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CollectionService, GenerateCollectionRequest, ManualGameDTO, MeterRow, MeterSnapshot } from 'src/app/core/services/collection.service';
import { CollectionSummaryDTO } from 'src/app/core/models/collectionSummaryDTO.model';
import { LocationService } from 'src/app/core/services/location.service';
import { Location } from 'src/app/core/models/location.model'; // adjust import path as needed
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SalesRepService } from 'src/app/core/services/sales-rep.service';
import { LoginService } from 'src/app/core/services/login.service';
import { UserType } from 'src/app/core/models/userType.models';
import * as html2pdf from 'html2pdf.js';
import { firstValueFrom } from 'rxjs';
type UIMeterRow = MeterRow & {
  currIn?: number;
  currOut?: number;
  readingTime?: string;
  use?: boolean;
};




@Component({
  selector: 'app-collection-modal',
  templateUrl: './collection-modal.component.html',
})


export class CollectionModalComponent implements OnInit {

  
  locations: Location[] = [];
  selectedLocationId!: string;
  allowNonPendingLocation = false;
  previousDate!: string;
  showManualGameSection = false;
  prevMetersByBoardId = new Map<string, MeterSnapshot>();
  meterRows: UIMeterRow[] = [];
  prevReadingTime!: string;  // `${previousDate}T00:00:00`
 currReadingTime: string = this.toLocalDateTimeLocal();

  splitPercentage: number = 0.20;

  collectionSummary?: CollectionSummaryDTO;
  isLoading = false;
  errorMessage = '';
  manualGames: ManualGameDTO[] = [];
 useMechanicalMeters: boolean = false; // ← add "= false"

// add to your component class
  boardsAtLocation: Array<{ id: string; assetTag: string; gameName?: string }> = [];
  assetTagToBoardId = new Map<string, string>();

  // selected in the meter modal
  meterAssetTag: string = '';  // what the user picks in the dropdown
  // you already have: meterIn, meterOut, meterReadingTime, useMechanicalMeters, etc.


    // NEW: toggle for meter-mode (bound to checkbox)
 

  // NEW: tiny meter entry form state (for the nested modal)
  meterBoardId: string = '';
  meterIn: number | null = null;
  meterOut: number | null = null;
  meterReadingTime: string = this.toLocalDateTimeLocal(); // "YYYY-MM-DDTHH:mm"
  
  private readonly METER_DIGITS = 7;
  private assignedSalesRepIds = new Set<string>();
  




  constructor(
    private collectionService: CollectionService,
    private locationService: LocationService,
    private salesRepService: SalesRepService,
    private loginService: LoginService,
    public activeModal: NgbActiveModal,
    private modal: NgbModal        // NEW
  ) {}
async ngOnInit(): Promise<void> {
  try {
    await this.loadCollectorAssignments();
    const pendingLocations = await firstValueFrom(this.collectionService.getPendingLocationsForCollector());
    this.locations = this.filterLocationsForCollector(pendingLocations || [])
      .filter((location) => this.isEnabledLocation(location));

    if (this.allowNonPendingLocation) {
      this.locations = await this.loadLocationsForManualGeneration(this.locations);
    }

    if (this.selectedLocationId && !this.locations.some((location) => location.id === this.selectedLocationId)) {
      const selectedLocation = this.allowNonPendingLocation
        ? await this.loadSelectedLocation(this.selectedLocationId)
        : null;

      if (selectedLocation && this.canGenerateForLocation(selectedLocation)) {
        this.locations = [selectedLocation, ...this.locations];
      } else {
        this.selectedLocationId = '';
        this.errorMessage = 'This location is no longer pending or has been disabled.';
      }
    }

    // If modal opened with pre-selected location, load its latest collection
    if (this.selectedLocationId) {
      this.onLocationChange(); // no need for setTimeout
    }
  } catch {
    this.errorMessage = 'Failed to load locations.';
  }
}

private async loadLocationsForManualGeneration(pendingLocations: Location[]): Promise<Location[]> {
  try {
    const allLocations = await this.locationService.getAll();
    const mergedById = new Map<string, Location>();

    [...(pendingLocations || []), ...(allLocations || [])].forEach((location) => {
      if (location?.id && this.canGenerateForLocation(location)) {
        mergedById.set(location.id, location);
      }
    });

    return Array.from(mergedById.values())
      .sort((a, b) => (a.locationName || '').localeCompare(b.locationName || ''));
  } catch {
    return pendingLocations;
  }
}

private async loadSelectedLocation(locationId: string): Promise<Location | null> {
  try {
    return await this.locationService.getById(locationId);
  } catch {
    return null;
  }
}

private canGenerateForLocation(location: Location): boolean {
  if (!this.isEnabledLocation(location)) {
    return false;
  }

  const type = this.loginService.userType;
  if (['ROOT', 'INTERNAL', 'TECH'].includes(type)) {
    return true;
  }

  if (type === UserType.EXTERNAL) {
    return this.normalizeId(location.salesRep?.id) === this.normalizeId(this.loginService.userId);
  }

  return this.filterLocationsForCollector([location]).length > 0;
}

private async loadCollectorAssignments(): Promise<void> {
  if (this.loginService.userType !== UserType.COLLECTOR || !this.loginService.userId) {
    return;
  }

  const salesReps = await this.salesRepService.getAll();
  this.assignedSalesRepIds = new Set(
    (salesReps || [])
      .filter((rep) => this.normalizeId(rep.collectorId || rep.collector?.id) === this.normalizeId(this.loginService.userId))
      .map((rep) => this.normalizeId(rep.id))
      .filter((id) => !!id)
  );
}

private filterLocationsForCollector(locations: Location[]): Location[] {
  if (this.loginService.userType !== UserType.COLLECTOR) {
    return locations;
  }

  if (!this.assignedSalesRepIds.size) {
    return [];
  }

  return locations.filter((location) => this.assignedSalesRepIds.has(this.normalizeId(location?.salesRep?.id)));
}

private isEnabledLocation(location: Location): boolean {
  return location?.enabled === true || location?.enabled === 1 || `${location?.enabled}`.toLowerCase() === 'true';
}

private normalizeId(value?: string | null): string {
  return (value || '').replace(/-/g, '').toLowerCase();
}


  generate(): void {
    if (!this.selectedLocationId || !this.previousDate) {
      this.errorMessage = 'Please fill in all fields.';
      return;
    }

    const request: GenerateCollectionRequest = {
      locationId: this.selectedLocationId,
      previousDate: `${this.previousDate}T00:00:00`,
      splitPercentage: this.splitPercentage,
      manualGames: this.manualGames,
      useMechanicalMeters: this.useMechanicalMeters   // ← must be present
    };

    this.isLoading = true;
    this.errorMessage = '';
    this.collectionService.generateCollection(request).subscribe({
      next: (result) => { this.collectionSummary = result; this.isLoading = false; },
      error: (err) => {
        // show backend message (400) instead of generic 500
        this.errorMessage = (typeof err?.error === 'string' && err.error)
          ? err.error
          : 'Failed to generate collection.';
        this.isLoading = false;
      }
    });
  }
  

    close(): void {
    this.activeModal.dismiss(); // or .close() if you're returning data
}

    exportToPDF(): void {
      const element = document.getElementById('collection-summary');

      if (!element) return;

      const options = {
        margin:       0.5,
        filename:     'collection-summary.pdf',
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2 },
        jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      html2pdf().from(element).set(options).save();
    }

loadLatestCollectionData(locationId: string): void {
  this.collectionService.getLatestCollection(locationId).subscribe({
    next: (latest) => {
      console.log('🔥 Latest Collection from API:', latest); // ← DEBUG this

      if (latest?.currentCollectionDate) {
        this.previousDate = latest.currentCollectionDate.substring(0, 10); // YYYY-MM-DD
      }

      if (latest?.splitPercentage !== undefined && latest.splitPercentage !== null) {
        this.splitPercentage = latest.splitPercentage;
      }
    },
    error: (err) => {
      console.warn('❌ No previous collection found or error occurred.', err);
    }
  });
}


async onLocationChange(): Promise<void> {
  if (!this.selectedLocationId) return;

  this.loadLatestCollectionData(this.selectedLocationId);

  try {
    const boards = await this.locationService.getBoardsByLocationId(this.selectedLocationId);
    this.boardsAtLocation = boards ?? [];
  } catch {
    this.boardsAtLocation = [];
  }

  // if user already ticked the checkbox, refresh previous meters now
  if (this.useMechanicalMeters && this.previousDate) {
    this.loadMetersGrid();         // ← show the grid
    this.onUseMechanicalMetersChanged();
  }
}


  addManualGame(): void {
  this.manualGames.push({ gameName: '', cashIn: 0, cashOut: 0 });
}

removeManualGame(index: number): void {
  if (this.manualGames.length > 1) {
    this.manualGames.splice(index, 1);
  }
}

// Make "now" in datetime-local format (no seconds; browser adds them)
private toLocalDateTimeLocal(d: Date = new Date()): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// If <input type="datetime-local"> returns "YYYY-MM-DDTHH:mm", ensure ":ss"
private normalizeDateTimeLocal(s?: string): string | undefined {
  if (!s) return undefined;
  return s.length === 16 ? `${s}:00` : s;
}

openMeterModal(tpl: any) {
  this.meterReadingTime = this.previousDate
    ? `${this.previousDate}T00:00`           // baseline → counts as “previous” right away
    : this.toLocalDateTimeLocal();

  if (!this.meterBoardId && this.boardsAtLocation.length) {
    this.meterBoardId = this.boardsAtLocation[0].id; // preselect
  }
  this.modal.open(tpl, { size: 'sm' });
}

saveMeters(activeModal: any) {
  if (!this.meterBoardId || this.meterIn == null || this.meterOut == null) return;

  const readingTime = this.normalizeDateTimeLocal(this.meterReadingTime);

  this.collectionService.upsertMechanicalMeter(this.meterBoardId, {
    meterIn: Number(this.meterIn),
    meterOut: Number(this.meterOut),
    readingTime // optional; backend defaults to now if omitted
  }).subscribe({
    next: () => {
      this.errorMessage = '';
      activeModal.close();
    },
    error: (err) => {
      this.errorMessage = (typeof err?.error === 'string' && err.error)
        ? err.error
        : 'Failed to save meters.';
    }
  });
}

// when the checkbox is toggled or when location/previousDate changes, call this
onUseMechanicalMetersChanged(): void {
  if (!this.useMechanicalMeters) {
    this.prevMetersByBoardId.clear();
    return;
  }
  if (!this.selectedLocationId || !this.previousDate) {
    this.errorMessage = 'Select location and previous date to load meters.';
    return;
  }

  const prevIso = `${this.previousDate}T00:00:00`;
  this.collectionService.getMetersSnapshot(this.selectedLocationId, prevIso).subscribe({
    next: rows => {
      this.prevMetersByBoardId.clear();
      rows.forEach(r => this.prevMetersByBoardId.set(r.boardId, r));

      // default board in modal
      if (!this.meterBoardId && this.boardsAtLocation.length) {
        const preferred = this.boardsAtLocation.find(b => this.prevMetersByBoardId.has(b.id)) ?? this.boardsAtLocation[0];
        this.meterBoardId = preferred.id;
      }
      this.applyPrevToForm(this.meterBoardId);
    },
    error: () => this.errorMessage = 'Failed to load previous meters.'
  });
}

// apply previous snapshot (if exists) to the modal inputs
applyPrevToForm(boardId?: string): void {
  if (!boardId) return;
  const m = this.prevMetersByBoardId.get(boardId);
  if (!m) { return; }
  this.meterIn = m.prevIn ?? null;
  this.meterOut = m.prevOut ?? null;
  this.meterReadingTime = m.prevTime
    ? m.prevTime.substring(0, 16)
    : `${this.previousDate}T00:00`;
}

// allow saving as “previous” or “current”
saveMetersAs(mode: 'previous' | 'current', activeModal: any) {
  if (!this.meterBoardId || this.meterIn == null || this.meterOut == null) {
    this.errorMessage = 'Board, Meter In, and Meter Out are required.';
    return;
  }

  let readingTime: string | undefined;
  if (mode === 'previous') {
    if (!this.previousDate) {
      this.errorMessage = 'Set Previous Date first.';
      return;
    }
    readingTime = `${this.previousDate}T00:00:00`;
  } else {
    readingTime = this.normalizeDateTimeLocal(this.meterReadingTime); // or omit to default to now
  }

  this.collectionService.upsertMechanicalMeter(this.meterBoardId, {
    meterIn: Number(this.meterIn),
    meterOut: Number(this.meterOut),
    readingTime
  }).subscribe({
    next: () => {
      this.errorMessage = '';
      if (this.useMechanicalMeters) this.onUseMechanicalMetersChanged(); // refresh cache
      activeModal.close();
    },
    error: (err) => {
      this.errorMessage = (typeof err?.error === 'string' && err.error)
        ? err.error
        : 'Failed to save meters.';
    }
  });
}

private padSeconds(s: string) { return s?.length === 16 ? s + ':00' : s; }

// mirror server rollover (set to 7 if you fixed server at 7)
meterDelta(curr?: number, prev?: number) {
  if (curr == null || prev == null) return 0;
  const ci = Math.floor(curr), pi = Math.floor(prev);
  if (ci >= pi) return ci - pi;
  const mod = Math.pow(10, this.METER_DIGITS);
  return (mod - pi) + ci;
}
loadMetersGrid(): void {
  if (!this.selectedLocationId) return;

  const afterPaint = () => setTimeout(() => this.scrollToMetersGrid(), 0);

  // If we don't have a previous date yet, still show the grid seeded from boards
  if (!this.previousDate) {
    if (!this.boardsAtLocation?.length) {
      this.locationService.getBoardsByLocationId(this.selectedLocationId)
        .then(boards => {
          this.boardsAtLocation = boards ?? [];
          this.meterRows = this.seedRowsFromBoards(); // UIMeterRow[]
          afterPaint();
        })
        .catch(() => {
          this.boardsAtLocation = [];
          this.meterRows = [];
        });
    } else {
      this.meterRows = this.seedRowsFromBoards(); // UIMeterRow[]
      afterPaint();
    }
    return;
  }

  // We have a previous date → try to load snapshots for all boards
  const prevISO = `${this.previousDate}T00:00:00`;
  this.collectionService.getMechanicalMeters(this.selectedLocationId, prevISO)
    .subscribe({
      next: rows => {
        // Normalize to UIMeterRow[] so r.currIn / r.currOut exist for typing
        let base: UIMeterRow[] = (rows && rows.length)
          ? rows.map(r => ({ ...r } as UIMeterRow))
          : this.seedRowsFromBoards(); // already UIMeterRow[]

        const minute = (this.currReadingTime || this.toLocalDateTimeLocal()).slice(0, 16);

        this.meterRows = base.map(r => ({
          ...r,
          currIn: null,
          currOut: null,
          readingTime: r.readingTime ?? minute,
          use: r.use ?? true,
        }));

        afterPaint();
      },
      error: () => {
        this.errorMessage = 'Failed to load meters for this location.';
        this.meterRows = this.seedRowsFromBoards(); // UIMeterRow[]
        afterPaint();
      }
    });
}







applyTimeToAll(dtLocal: string) {
  const withSec = this.padSeconds(dtLocal); // "YYYY-MM-DDTHH:mm:ss"
  const minuteOnly = (withSec || '').slice(0, 16);
  this.meterRows.forEach(r => r.readingTime = minuteOnly);
}


saveAllMeters(mode: 'previous' | 'current') {
  if (!this.selectedLocationId) return;

  // readingTime for "previous" must exist; enforce here
  if (mode === 'previous' && !this.previousDate) {
    this.errorMessage = 'Set Previous Collection Date first.';
    return;
  }

  const nowLocal = this.toLocalDateTimeLocal();   // "YYYY-MM-DDTHH:mm"
  const rtForRow = (r: UIMeterRow) =>
    mode === 'previous'
      ? `${this.previousDate}T00:00:00`
      : this.padSeconds(r.readingTime || this.currReadingTime || nowLocal);

  // Only include rows that are checked and have finite numbers
  const dirty = this.meterRows.filter(r => !!r.use);

  const bad = dirty.find(r => !Number.isFinite(+r.currIn!) || !Number.isFinite(+r.currOut!));
  if (bad) {
    this.errorMessage = `Row ${bad.assetTag}: enter valid numbers for Curr In/Out.`;
    return;
  }

  const payload = dirty.map(r => ({
    boardId: r.boardId,
    meterIn: Math.floor(Number(r.currIn)),
    meterOut: Math.floor(Number(r.currOut)),
    readingTime: rtForRow(r) // -> always "YYYY-MM-DDTHH:mm:ss"
  }));

  if (!payload.length) {
    this.errorMessage = 'Nothing to save. Tick rows and enter current meters.';
    return;
  }

  // Optional: quick visibility for debugging
  // console.log('[bulk payload]', payload);

  this.isLoading = true;
  this.collectionService.bulkUpsertMechanicalMeters(payload).subscribe({
    next: () => {
      this.isLoading = false;
      this.errorMessage = '';
      this.loadMetersGrid(); // refresh “prev/latest”
    },
    error: (err) => {
      this.isLoading = false;
      // show backend-supplied message if any
      this.errorMessage =
        (typeof err?.error === 'string' && err.error) ||
        err?.error?.message ||
        'Failed to save meters.';
    }
  });
}


saveMetersBulk(): void {
  const payload = this.meterRows
    .filter((r: any) => r.use && r.currIn != null && r.currOut != null && !!r.readingTime)
    .map((r: any) => ({
      boardId: r.boardId,
      meterIn: Number(r.currIn),
      meterOut: Number(r.currOut),
      readingTime: this.normalizeDateTimeLocal(r.readingTime)!
    }));

  if (!payload.length) {
    this.errorMessage = 'Select at least one row and fill current meters.';
    return;
  }

  this.isLoading = true;
  this.collectionService.bulkUpsertMechanicalMeters(payload).subscribe({
    next: () => {
      this.isLoading = false;
      this.errorMessage = '';
      this.loadMetersGrid(); // refresh “prev/latest”
    },
    error: (err) => {
      this.isLoading = false;
      this.errorMessage = (typeof err?.error === 'string' && err.error)
        ? err.error
        : 'Failed to save meters.';
    }
  });
}

get hasAnyPrev(): boolean {
  return this.meterRows?.some(r => r.prevIn != null || r.prevOut != null) ?? false;
}

@ViewChild('metersGridTop', { static: false }) metersGridTop?: ElementRef;
scrollToMetersGrid() {
  this.metersGridTop?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

private seedRowsFromBoards(): UIMeterRow[] {
  const minute = (this.currReadingTime || this.toLocalDateTimeLocal()).slice(0, 16);
  return (this.boardsAtLocation || []).map(b => ({
    boardId: b.id,
    assetTag: b.assetTag,
    gameName: b.gameName,
    // no snapshots yet:
    prevIn: undefined, prevOut: undefined, prevTime: undefined,
    latestIn: undefined, latestOut: undefined, latestTime: undefined,
    // editable fields:
    currIn: null,
    currOut: null,
    readingTime: minute,
    use: true,
  }));
}

onMetersToggle() {
  if (this.useMechanicalMeters) {
    this.loadMetersGrid();
  }
}

















}
